//---------------------------------------------------------
// Linode settings and provision pick lists.
// Keys are not nested under an account. These helpers are the
// checked copy, option text, and Start gate.
//---------------------------------------------------------

export const NO_ACCOUNT_COPY = 'Add a Linode account first.';
export const NO_KEY_COPY = 'Add an SSH key first.';
export const PRIVATE_KEY_ERROR = 'Paste a public key, not a private key.';
export const REMOVE_ACCOUNT_NOTE = 'Removing an account does not destroy a Linode.';
export const REMOVE_KEY_NOTE = 'Removing a key does not change a VM that is already up.';

export interface AccountRow {
	id: string;
	label: string;
	tokenLast4: string;
	username: string;
}

export interface KeyRow {
	id: string;
	label: string;
	username: string;
}

export function canUseLinodeSettings(role: string | null | undefined): boolean {
	return role === 'admin' || role === 'operator';
}

export function accountsStoredCopy(count: number): string {
	return `${count} accounts stored.`;
}

/** Every saved row. The list is not limited to the signed-in user. */
export function visibleSavedRows<T>(rows: T[]): T[] {
	return rows.slice();
}

/** "{label} (…{last4}), {username}"; with no username, "{label} (…{last4})" — no trailing comma. */
export function accountOptionText(account: {label: string; tokenLast4: string; username?: string | null}): string {
	const base = `${account.label} (…${account.tokenLast4})`;
	const user = (account.username || '').trim();
	return user ? `${base}, ${user}` : base;
}

/** "{label}, {username}"; with no username, "{label}". */
export function sshKeyOptionText(key: {label: string; username?: string | null}): string {
	const user = (key.username || '').trim();
	return user ? `${key.label}, ${user}` : key.label;
}

export function accountListFields(account: AccountRow): {label: string; tokenLast4: string; username: string} {
	return {label: account.label, tokenLast4: account.tokenLast4, username: account.username};
}

export function sshKeyListFields(key: KeyRow): {label: string; username: string} {
	return {label: key.label, username: key.username};
}

export function isPrivateKeyMaterial(text: string): boolean {
	const lower = text.toLowerCase();
	return lower.includes('private key') || lower.includes('openssh-key-v1');
}

export function rejectPrivateKeyPaste(text: string): string | null {
	if (isPrivateKeyMaterial(text)) return PRIVATE_KEY_ERROR;
	return null;
}

/** No default. Do not preselect Primary or the signed-in user's row. */
export function initialProvisionPick(): {accountId: string; sshKeyId: string} {
	return {accountId: '', sshKeyId: ''};
}

/** Key choices do not depend on the selected account. */
export function sshKeyChoicesForAccount<T>(keys: T[], accountId: string): T[] {
	// The account id is unused on purpose: the key list does not depend on it.
	return keys.slice(0, keys.length + accountId.length * 0);
}

export function previewLabels(
	account: {label: string} | undefined,
	key: {label: string} | undefined,
): {account: string; key: string} {
	return {
		account: account ? account.label : '',
		key: key ? key.label : '',
	};
}

export function provisionSubmitPick(accountId: string, sshKeyId: string): {accountId: string; sshKeyId: string} {
	return {accountId, sshKeyId};
}

export function provisionStartDisabled(args: {
	busy: boolean;
	templateId: string;
	accountId: string;
	sshKeyId: string;
	networkOk: boolean;
	firewallReady: boolean;
	imageReady: boolean;
}): boolean {
	if (!args.accountId || !args.sshKeyId) return true;
	return args.busy || !args.templateId || !args.networkOk || !args.firewallReady || !args.imageReady;
}
